import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { useGetClientOrders } from '@/api/market-shop/orders/get-client-orders'
import { Button } from '@/components/business/market-shop/button'
import { PopupWindow } from '@/components/business/market-shop/popup-window'
import { Typography } from '@/components/ui/typography'
import { PopupRequiredProps, popupFactory } from '@/factories'
import { useIntersectionSentinel } from '@/hooks'
import { getOrderStatusToBadgeLabelMap, MarketShopClientOrderModel } from '@/models'
import { EmptyState } from '@/pages/market-shop/empty-state'
import { OrderDetails } from '@/pages/market-shop/order-details'

import { marketShopOrdersInitialFilters } from './orders-history.constants'
import * as S from './orders-history.styled'
import { OrderContainer } from './orders-history.styled'

export type OrdersHistoryProps = PopupRequiredProps

export const OrdersHistory = ({ onClose }: OrdersHistoryProps) => {
  const { t } = useTranslation(['market-shop'])
  const { isResidentFlow } = useGetSettingsByToken()

  const {
    data: clientOrders,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useGetClientOrders({
    data: marketShopOrdersInitialFilters,
    enabled: isResidentFlow,
  })

  const flatOrders = useMemo(() => {
    return clientOrders?.pages.flatMap((p) => p.data) ?? []
  }, [clientOrders])
  const flatOrdersAmount = flatOrders.length

  // NOTE: we do not use `rootRef`, due to `OrdersHistory` renders `PopupWindow` with `position: fixed`
  // Otherwise incorrect reference will be used during intersection detection and orders will be fetched indefinitely
  const { sentinelRef } = useIntersectionSentinel<HTMLDivElement, HTMLDivElement>(
    () => {
      if (!hasNextPage) return
      void fetchNextPage()
    },
    { deps: [hasNextPage, flatOrdersAmount] },
  )

  const handleOrderClick = (order: MarketShopClientOrderModel) => {
    popupFactory.open(OrderDetails, { order }, { suspendCurrent: true })
  }

  return (
    <PopupWindow lightBg={true} onClose={onClose}>
      {() => (
        <>
          <S.Content>
            {!isFetching && !flatOrdersAmount && (
              <EmptyState
                title={t('market-shop:ordersHistory.emptyTitle')}
                message={t('market-shop:ordersHistory.emptyDescription')}
              />
            )}
            {flatOrders.map((order) => (
              <OrderContainer key={order.id} onClick={() => handleOrderClick(order)}>
                <Typography
                  as="span"
                  size="text-medium"
                  weight="medium"
                  color="black800"
                  breakLines={1}
                >
                  {t('market-shop:ordersHistory.orderNumber', { id: order.id })}
                </Typography>
                <S.BadgeText
                  forwardedAs="span"
                  size="text-small"
                  weight="medium"
                  whiteSpace="nowrap"
                  $status={order.status}
                >
                  {getOrderStatusToBadgeLabelMap(t)[order.status](order)}
                </S.BadgeText>
              </OrderContainer>
            ))}
            <div ref={sentinelRef} />
            {isFetching && <S.Loader size="small" $hasContent={!!flatOrdersAmount} />}
          </S.Content>
          <S.Controls>
            <Button use="secondary-gray" size="large" onClick={onClose}>
              {t('market-shop:ordersHistory.close')}
            </Button>
          </S.Controls>
        </>
      )}
    </PopupWindow>
  )
}
